import { debounce } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import { Context } from "../../index";
import { PRIVACY_POLICY } from "../../utils/consts";
import CheckboxUI from "../UI/CheckboxUI/CheckboxUI";
import ErrorMessage from "../UI/ErrorMessage/ErrorMessage";
import InputUI from "../UI/Input/Input";
import './styles.scss';
import { useMediaQuery } from "../../hooks/useMediaQuery";

const RU_PHONE_MASK = "+7 (999) 999-99-99";

function formatPhone(phone) {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
        return `+7 (${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7,9)}-${digits.slice(9,11)}`;
    }
    return phone;
}

export default function RecipientForm({ setIsValid, closeModalStepTwo }) {
    const { delivery, user, basket } = useContext(Context);
    // Publish the contract of this actual input, not a phone-confirmation or
    // checkout grant. Legacy forms still provide their own international mask.
    useEffect(() => {basket.setCountryAndMask({mask: RU_PHONE_MASK, number: 11});}, [basket]);
    const isTablet = useMediaQuery('(max-width: 768px)')
    const [formValues, setFormValues] = useState({
        surname: delivery.customerInfo?.surname || user.user.surname || '',
        name: delivery.customerInfo?.name || user.user.name || '',
        phone: delivery.customerInfo?.phone || formatPhone(user.user.telephone) || '',
        email: delivery.customerInfo?.email || user.user.email || '',
        subscribeNews: delivery.customerInfo?.subscribeNews || false,
        agreePrivacy: delivery.customerInfo?.agreePrivacy || false,
    });

    const {
        watch,
        control,
        handleSubmit,
        register,
        formState: { errors, isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ...delivery.customerInfo,
            surname:  user.user.surname ?? '',
            name: user.user.name ?? '',
            telephone: user.user.telephone ?? '',
            email: user.user.email ?? '',
            subscribeNews: false,
            agreePrivacy: false,
        },
    });

    const debouncedSave = useRef(
        debounce((data) => {
            const info = delivery.customerInfo;
            delivery.setCustomerInfo({
                ...info,
                name: data.name,
                surname: data.surname,
                telephone: data.telephone,
                email: data.email,
                agreePrivacy: data.agreePrivacy,
            });
        }, 500)
    ).current;

    const onSubmit = (data) => {
        delivery.setCustomerInfo(data);
    };

    const handleFieldFocus = (fieldName) => {
        if (!user.isAuth && !user.authShowed) {

            if (isTablet && closeModalStepTwo) {
                closeModalStepTwo();
            }

            user.setStepModals('registration');
            user.setOpenAuthModal(1);
            user.setShadowOverlay(true);
        }
    };

    const phoneFormat = function(telephone){
        if(telephone.length > 11){
            return telephone
        }
        const cleaned = ('' + telephone).replace(/\D/g, '');

        const match = cleaned.match(/^(\d|)?(\d{3})(\d{3})(\d{2})(\d{2})$/);

        if (match) {
            let intlCode = (match[1] ? '+'+match[1]+' ' : '')
            return [intlCode, '(', match[2], ') ', match[3], '-', match[4], '-', match[5]].join('')
        }
    }

    useEffect(() => {
        const subscription = watch((data) => {
            data.telephone =  phoneFormat(data.telephone)
            setFormValues(data);
            debouncedSave(data);
        });

        return () => {
            subscription.unsubscribe();
            debouncedSave.cancel();
        };
    }, [watch, debouncedSave]);

    useEffect(() => {
        if (setIsValid) {
            setIsValid(isValid);
        }
    }, [isValid, setIsValid]);

    return (
        <form className="form-recipient" onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="form-recipient__item">
                <InputUI
                    placeholder="Фамилия*"
                    value={formValues.surname}
                    {...register("surname", { required: "Это поле обязательно" })}
                    onFocus={() => handleFieldFocus("surname")}
                    readOnly={!user.isAuth}
                />
                {errors.surname && <ErrorMessage>{errors.surname.message}</ErrorMessage>}
            </div>

            <div className="form-recipient__item">
                <InputUI
                    value={formValues.name}
                    placeholder="Имя*"
                    onFocus={() => handleFieldFocus()}
                    readOnly={!user.isAuth}
                    {...register("name", { required: "Это поле обязательно" })}
                />
                {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </div>

            <div className="form-recipient__item">
                <Controller
                    name="telephone"
                    control={control}
                    rules={{
                        required: "Это поле обязательно",
                        pattern: {
                            value: /^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/,
                            message: "Введите номер в формате +7 (XXX) XXX-XX-XX",
                        },
                    }}
                    render={({ field }) => (
                        <InputMask
                            className="ui-input-phone"
                            mask={RU_PHONE_MASK}
                            maskChar={null}
                            {...field}
                            placeholder="Телефон*"
                            onFocus={() => handleFieldFocus()}
                            onChange={(e) => {
                                if (user.isAuth) field.onChange(e);
                            }}
                        />
                    )}
                />
                {errors.telephone && <ErrorMessage>{errors.telephone.message}</ErrorMessage>}
            </div>

            <div className="form-recipient__item">
                <InputUI
                    type="email"
                    placeholder="Email*"
                    value={formValues.email}
                    {...register("email", {
                        required: "Это поле обязательно",
                        pattern: {
                            value: /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/iu,
                            message: "Введите корректный email",
                        },
                    })}
                    onFocus={() => handleFieldFocus()}
                    readOnly={!user.isAuth}
                />
                {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </div>

            <div className="checkbox-group">
                <label>
                    <Controller
                        name="subscribeNews"
                        control={control}
                        render={({ field }) => (
                            <CheckboxUI
                                {...field}
                                checked={field.value}
                                onChange={(e) => {
                                    handleFieldFocus()
                                    if (user.isAuth) field.onChange(e);
                                }}
                            >
                                Подписаться на новости и скидки
                            </CheckboxUI>
                        )}
                    />
                </label>

                <label>
                    <Controller
                        name="agreePrivacy"
                        control={control}
                        rules={{ required: "Необходимо согласие на обработку данных" }}
                        render={({ field }) => (
                            <CheckboxUI
                                {...field}
                                checked={field.value}
                                onChange={(e) => {
                                    handleFieldFocus()
                                    if (user.isAuth) field.onChange(e);
                                }}
                            >
                                Я даю согласие на обработку персональных данных в соответствии с{' '}
                                <a href={PRIVACY_POLICY} target="_blank" rel="noopener noreferrer">
                                    Политикой обработки персональных данных
                                </a>
                            </CheckboxUI>
                        )}
                    />
                </label>
                {errors.agreePrivacy && <ErrorMessage>{errors.agreePrivacy.message}</ErrorMessage>}
            </div>
        </form>
    );
}
